import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from "@porchpass/ui";
import { updateNote } from "src/api/preApproval";
import type { PreApprovalNote } from "src/api/preApproval/types";

interface EditNoteModalProps {
  note: PreApprovalNote;
  externalId: string;
  onCancel: () => void;
  onSuccess: (note: PreApprovalNote) => void;
}

export function EditNoteModal({
  note,
  externalId,
  onCancel,
  onSuccess,
}: EditNoteModalProps) {
  const [content, setContent] = useState(note.note);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) {
      setError("Enter a note before saving.");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const updated = await updateNote(externalId, note.id, trimmed);
      onSuccess(updated);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Unable to update note right now.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit note</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <form
            id="edit-note-form"
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
            className="flex flex-col gap-4"
          >
            <Textarea
              label="Note"
              value={content}
              onChange={(event) => {
                setContent(event.target.value);
                if (error) {
                  setError(null);
                }
              }}
              rows={5}
              required
            />
            {error && <p className="text-sm text-[#B91C1C]">{error}</p>}
          </form>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-note-form"
            disabled={isSubmitting || !content.trim()}
          >
            {isSubmitting ? "Saving..." : "Save note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
